import { Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CuiDialogService } from '@innersource/common-ui-components';
import {
  DataType,
  isGraph,
  isImageFile,
  isTable,
  Tool,
  ToolJob,
  ToolParam,
} from '../../../../utils/models/tool.models';
import { ToolJobService } from '../../../../services/api-services/tools/tool-job.service';
import { DynamicTableComponent } from '../../../../utils/dynamic-table/dynamic-table.component';
import { EChartsOption } from '../../../../utils/graph/echart-types';
import { filterOptionalData } from '../../../../utils/table-helper';
import { GraphComponent } from '../../../../utils/graph/graph.component';
import {
  fileNameFromContentDisposition,
  fileNameFromValue,
  toArray,
  toRecord,
} from '../../../../utils/helper';
import { ToolJobPreviewDialogComponent } from '../tool-job-preview-dialog/tool-job-preview-dialog.component';
import { ToolJobResultItem } from './tool-job-results.types';
import { valueText } from '../../job-timeline/timeline-preview/timeline-preview.util';

@Component({
  selector: 'app-tool-job-results',
  templateUrl: './tool-job-results.component.html',
  styleUrls: ['./tool-job-results.component.scss'],
  imports: [DynamicTableComponent, GraphComponent],
})
export class ToolJobResultsComponent {
  /** Inputs. */
  readonly toolJob = input.required<ToolJob | null>();
  readonly tool = input.required<Tool>();
  readonly content = input<'results' | 'parameters'>('results');

  /** Services. */
  private readonly toolJobService = inject(ToolJobService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(CuiDialogService);

  /** Template Forwards. */
  readonly valueText = valueText;

  /** State. */
  readonly items = signal<ToolJobResultItem[]>([]);
  private readonly objectUrls = new Set<string>();
  private loadRequest = 0;

  private readonly outputs = this.toolJobService.getOutputs(() =>
    this.content() === 'results' ? this.toolJob()?.toolJobId : undefined
  );

  /** Returns the structured parameters or results for the current job. */
  private readonly data = computed<Record<string, unknown> | undefined>(() => {
    const job = this.toolJob();
    if (!job) return undefined;
    if (this.content() === 'parameters') return toRecord(job.funcInput);
    return typeof job.results === 'string' ? {} : toRecord(job.results);
  });

  /** Constructor. */
  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.objectUrls.forEach(url => URL.revokeObjectURL(url));
    });

    // Rebuild preview items whenever the selected job data changes.
    effect(() => {
      const toolConfig = this.tool();
      const data = this.data();
      const request = ++this.loadRequest;
      if (!data || Object.keys(data).length === 0) {
        this.items.set([]);
        return;
      }

      void this.buildItems(toolConfig, data).then(items => {
        if (request === this.loadRequest) this.items.set(items);
      });
    });
  }

  /** Opens the selected item in the preview dialog. */
  openPreview(item: ToolJobResultItem): void {
    const job = this.toolJob();
    const contextTitle = `${this.tool().name} | ${
      this.content() === 'parameters' ? 'Parameters' : `Run ${job?.toolJobId ?? ''}`
    }`;
    this.dialog.open(ToolJobPreviewDialogComponent, {
      data: {
        items: this.items(),
        initialIndex: this.items().indexOf(item),
        contextTitle,
        content: this.content(),
      },
      size: 'big',
      width: 'min(80rem, calc(100vw - 3rem))',
      maxWidth: 'calc(100vw - 3rem)',
    });
  }

  /** Opens an item preview from the keyboard. */
  openPreviewFromKeyboard(event: Event, item: ToolJobResultItem): void {
    event.preventDefault();
    this.openPreview(item);
  }

  /** Builds display items from job data. */
  private async buildItems(toolConfig: Tool, data: Record<string, unknown>) {
    const parameters = this.orderedParameters(toolConfig, data);
    const items: ToolJobResultItem[] = [];

    for (const parameter of parameters) {
      const value = data[parameter.name];
      switch (parameter.param.type) {
        case 'Value':
          items.push({
            kind: 'value',
            description: parameter.description,
            displayName: parameter.displayName,
            value: value as DataType | DataType[],
          });
          break;
        case 'File':
          items.push(...(await this.fileItems(parameter, value)));
          break;
        case 'Table':
          items.push(this.tableItem(parameter, value as Record<string, unknown>[]));
          break;
        case 'Graph':
          items.push(this.graphItem(parameter, value as EChartsOption));
          break;
      }
    }

    return items;
  }

  /** Returns configured parameters that have job data. */
  private orderedParameters(toolConfig: Tool, data: Record<string, unknown>): ToolParam[] {
    const configuredParameters =
      this.content() === 'results' ? toolConfig.outputs : toolConfig.inputs;
    const parametersByName = new Map(
      configuredParameters.map(parameter => [parameter.name, parameter])
    );
    return Object.keys(data).flatMap(name => {
      const parameter = parametersByName.get(name);
      return parameter ? [parameter] : [];
    });
  }

  /** Builds items for a file parameter. */
  private async fileItems(parameter: ToolParam, value: unknown): Promise<ToolJobResultItem[]> {
    if (this.content() === 'parameters') {
      return this.fileReferenceItems(parameter, value);
    }

    const outputIds = this.outputs.value();
    if (!outputIds) return this.fileLoadingItems(parameter, value);
    if (!(parameter.name in outputIds)) return this.fileReferenceItems(parameter, value);

    return this.downloadedFileItems(parameter, outputIds[parameter.name]);
  }

  /** Builds file items from file references. */
  private fileReferenceItems(parameter: ToolParam, value: unknown): ToolJobResultItem[] {
    return toArray(value).map((file, index) => ({
      kind: 'file',
      description: parameter.description,
      displayName: this.indexedName(parameter.displayName, index),
      fileName: fileNameFromValue(file) ?? parameter.displayName,
    }));
  }

  /** Downloads and builds file items. */
  private async downloadedFileItems(
    parameter: ToolParam,
    outputIds: unknown
  ): Promise<ToolJobResultItem[]> {
    const items = await Promise.all(
      toArray(outputIds).map((fileId, index) => this.downloadedFileItem(parameter, fileId, index))
    );
    return items.filter((item): item is ToolJobResultItem => item !== undefined);
  }

  /** Downloads and builds a single file item. */
  private async downloadedFileItem(
    parameter: ToolParam,
    fileId: unknown,
    index: number
  ): Promise<ToolJobResultItem | undefined> {
    const response = await this.toolJobService.getOutputById(Number(fileId));
    const displayName = this.indexedName(parameter.displayName, index);
    if (!response.body) {
      this.snackBar.open(`Error when loading file of ${parameter.displayName}.`);
      return undefined;
    }

    return this.fileItemFromResponse(parameter, response, displayName);
  }

  /** Creates a display item from a file response. */
  private async fileItemFromResponse(
    parameter: ToolParam,
    response: HttpResponse<Blob>,
    displayName: string
  ): Promise<ToolJobResultItem> {
    const file = response.body;
    if (!file) throw new Error('Expected a file response body.');
    const fileName =
      fileNameFromContentDisposition(response.headers.get('Content-Disposition')) ?? displayName;
    if (file.type === 'text/plain') {
      const src = parameter.allowDownload ? this.objectUrl(file) : undefined;
      return {
        kind: 'text',
        allowDownload: parameter.allowDownload,
        description: parameter.description,
        displayName,
        ...(src ? { src } : {}),
        text: await file.text(),
        fileName,
      };
    }

    if (isImageFile(parameter)) {
      return {
        kind: 'image',
        allowDownload: parameter.allowDownload,
        description: parameter.description,
        displayName,
        src: this.sanitizer.bypassSecurityTrustUrl(this.objectUrl(file)), // NOSONAR -- Content is generated internally and cannot contain 3rd party user input
        fileName,
      };
    }

    const src = parameter.allowDownload ? this.objectUrl(file) : undefined;
    return {
      kind: 'file',
      description: parameter.description,
      displayName,
      ...(src ? { src } : {}),
      fileName,
    };
  }

  /** Builds loading placeholders for files. */
  private fileLoadingItems(parameter: ToolParam, value: unknown): ToolJobResultItem[] {
    const files = toArray(value);
    const image = isImageFile(parameter);
    return (files.length ? files : [parameter.displayName]).map((file, index) => ({
      kind: 'file-loading',
      allowDownload: parameter.allowDownload,
      description: parameter.description,
      displayName: this.indexedName(parameter.displayName, index),
      fileName: fileNameFromValue(file) ?? parameter.displayName,
      image,
    }));
  }

  /** Creates a table display item. */
  private tableItem(parameter: ToolParam, value: Record<string, unknown>[]): ToolJobResultItem {
    if (!isTable(parameter.param)) throw new Error('Expected a table parameter.');
    const columnDef = parameter.param.columnDef.map(column => ({ ...column }));
    const tableData = value.map(row => ({ ...row }));
    filterOptionalData(columnDef, tableData);
    return {
      kind: 'table',
      displayName: parameter.displayName,
      description: parameter.description,
      columnDef,
      tableData,
    };
  }

  /** Creates a graph display item. */
  private graphItem(parameter: ToolParam, value: EChartsOption): ToolJobResultItem {
    if (!isGraph(parameter.param)) throw new Error('Expected a graph parameter.');
    return {
      kind: 'graph',
      displayName: parameter.displayName,
      description: parameter.description,
      options: value,
    };
  }

  /** Adds an index suffix to repeated names. */
  private indexedName(name: string, index: number): string {
    return index ? `${name} ${index + 1}` : name;
  }

  /** Creates and tracks an object URL. */
  private objectUrl(file: Blob): string {
    const url = URL.createObjectURL(file);
    this.objectUrls.add(url);
    return url;
  }
}
